import React, { useState, ChangeEvent } from "react";
import { useNavigate } from "react-router-dom";
import {
  Box,
  Button,

  TextField,
  Select,
  MenuItem,
  Typography,
  CardMedia,
  Input,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import { LocalizationProvider } from "@mui/x-date-pickers";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import dayjs, { Dayjs } from "dayjs";

const AddPortfolio = () => {
  const navigate = useNavigate();

  const [category, setCategory] = useState("default");
  const [subCategory, setSubCategory] = useState("default");
  const [images, setImages] = useState<(string | null)[]>(Array(6).fill(null));
  const [video, setVideo] = useState<string | null>(null);
  const [completionDate, setCompletionDate] = useState<Dayjs | null>(dayjs());

  // Handle image upload

  const handleRemoveImage = (index: number) => {
    const newImages = [...images];
    newImages[index] = null;
    setImages(newImages);
  };

  // Handle video upload
  const handleVideoChange = (event: ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (file) {
      const videoUrl = URL.createObjectURL(file);
      setVideo(videoUrl);
    }
  };

  const handleRemoveVideo = () => setVideo(null);

  return (
    <>
      <div className="main_title">
        <h1>Add Portfolio</h1>
        <p>
          <button onClick={() => navigate("/dashboard")}>Dashboard</button> -{" "}
          <button onClick={() => navigate("/portfolio")}>Portfolio Management</button> - Add Portfolio
        </p>
      </div>

      <div className="cards custom_form_details">
        <div className="form">
          <div className="gap_m w_100">
            {/* Title of Work */}
            <div className="control_group w_50">
              <label>Title of Work</label>
              <TextField
                placeholder="E.g., Wedding Photoshoot – Dubai"
                fullWidth
                hiddenLabel
              />
            </div>

            {/* Description */}
            <div className="control_group w_50">
              <label>Description</label>
              <TextField
                placeholder="Provide a brief description of the project..."
                fullWidth
                multiline
                minRows={3}
              />
            </div>

            {/* Category */}
            <div className="control_group w_50">
              <label>Category</label>
              <Select
                fullWidth
                value={category}
                onChange={(e) => setCategory(e.target.value)}
              >
                <MenuItem disabled value="default">
                  Select Category
                </MenuItem>
                <MenuItem value="photography">Photography</MenuItem>
                <MenuItem value="design">Design</MenuItem>
                <MenuItem value="art">Art</MenuItem>
              </Select>
            </div>

            {/* Sub-category */}
            <div className="control_group w_50">
              <label>Sub-category</label>
              <Select
                fullWidth
                value={subCategory}
                onChange={(e) => setSubCategory(e.target.value)}
              >
                <MenuItem disabled value="default">
                  Select Sub-category
                </MenuItem>
                <MenuItem value="wedding">Wedding</MenuItem>
                <MenuItem value="product">Product</MenuItem>
                <MenuItem value="event">Event</MenuItem>
              </Select>
            </div>

            {/* Date of Completion */}
            <div className="control_group w_50">
              <label>Date of Completion</label>
              <LocalizationProvider dateAdapter={AdapterDayjs}>
                <DatePicker
                  value={completionDate}
                  onChange={(newValue) => setCompletionDate(newValue)}
                  slotProps={{
                    textField: {
                      fullWidth: true,
                      placeholder: "Select completion date",
                    },
                  }}
                />
              </LocalizationProvider>
            </div>
          </div>
        </div>
      </div>

      {/* Media Upload Section */}
      <div className="cards upload_section">
        <Typography variant="h6" fontWeight={600} mb={2}>
          Upload Media
        </Typography>

        {/* Images Upload */}
        <Typography variant="subtitle1" fontWeight={500} mb={1}>
          Upload Images (Multiple Allowed)
        </Typography>

        <Box
          display="grid"
          gridTemplateColumns="repeat(auto-fill, 120px)"
          gap={2}
          mt={1}
        >
          {images.map((image, i) => (
            <div key={i}>
              {image ? (
                <div className="upload_image">
                  <div className="upload_image_holder square">
                    <figure>
                      <CardMedia component="img" image={image} alt={`upload-${i}`} />
                    </figure>
                    <CloseIcon onClick={() => handleRemoveImage(i)} />
                  </div>
                </div>
              ) : (
                <label
                  className="upload_image"
                  htmlFor={`icon-button-file-${i}`}
                >
                  <Input
                    sx={{ display: "none" }}
                    id={`icon-button-file-${i}`}
                    type="file"
                    inputProps={{
                      accept: "image/*",
                    }}
                  />
                  <span className="upload_image_holder square">
                    <figure className="add_img">
                      <img src="/static/images/placeholder.png" alt="icon" />
                      <span className="upload_img_icon">
                        <img src="/static/images/edit2_icon.svg" alt="edit" />
                      </span>
                    </figure>
                  </span>
                </label>
              )}
            </div>
          ))}
        </Box>

        {/* Video Upload */}
        <Box mt={4}>
          <Typography variant="subtitle1" fontWeight={500} mb={1}>
            Upload Video (Optional)
          </Typography>

          {video ? (
            <div className="upload_image video_upload">
              <video
                width="220"
                height="140"
                controls
                style={{ borderRadius: "8px" }}
              >
                <source src={video} type="video/mp4" />
              </video>
              <CloseIcon onClick={handleRemoveVideo} />
            </div>
          ) : (
            <label htmlFor="video-upload" className="upload_image">
              <Input
                id="video-upload"
                type="file"
                sx={{ display: "none" }}
                inputProps={{
                  accept: "video/*",
                }}
                onChange={handleVideoChange}
              />
              <span className="upload_image_holder square">
                <figure className="add_img">
                  <img src="/static/images/placeholder.png" alt="icon" />
                  <span className="upload_img_icon">
                    <img src="/static/images/edit2_icon.svg" alt="edit" />
                  </span>
                </figure>
              </span>
            </label>
          )}
        </Box>
        {/* Confirmation Checkboxes */}
        <Box mt={4}>
          <div className="control_group checkbox_group">
            <label style={{ display: "flex", alignItems: "flex-start", gap: "10px" }}>
              <input type="checkbox" />
              <span>
                I confirm that I own the rights to these photos and services, and that I
                have written consent from any identifiable people shown to use these photos.
              </span>
            </label>
          </div>

          <div className="control_group checkbox_group">
            <label style={{ display: "flex", alignItems: "flex-start", gap: "10px" }}>
              <input type="checkbox" />
              <span>I confirm the uploaded creatives meet the guidelines.</span>
            </label>
          </div>

          <div className="control_group checkbox_group">
            <label style={{ display: "flex", alignItems: "flex-start", gap: "10px" }}>
              <input type="checkbox" />
              <span>I agree to the Marketplace Advertising Terms.</span>
            </label>
          </div>

          {/* View Guidelines Button */}
          <Box mt={2}>
            <Button variant="outlined" className="btn btn_secondary">
              View Guidelines
            </Button>
          </Box>
        </Box>

        {/* Save Button */}
        <div className="w_100 mt_20">
          <Button className="btn btn_primary">
            Save Portfolio
          </Button>
        </div>
      </div>
    </>
  );
};

export default AddPortfolio;
